<template>
  <nav class="headerS">
    <div class="navbar-main">
      <!-- Left: Back button glued to left edge -->
      <div class="nav-left">
        <button v-if="$route.fullPath && canGoBack" type="button" class="back-link" data-tour="nav-back" @click.prevent="goBack" aria-label="Voltar">
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Center: Logos and menus centered -->
      <div class="nav-center">
        <button
          v-if="helpIconsEnabled && isLoggedIn && isCompanyAdmin"
          class="help-circle help-icon"
          data-tour="nav-left-help"
          @click.prevent="startNavTourLeft"
          title="Tour da navegação"
          aria-label="Tour da navegação"
          type="button"
        >
          <i class="fas fa-question-circle" aria-hidden="true"></i>
        </button>
        <a href="/" data-tour="nav-logo-due">
          <img v-if="showDueLogo" :src="dueLogoUrl" alt="DUE logo" class="logoHome" />
        </a>
        <a v-if="hasCliente" :href="'/' + clienteParam" data-tour="nav-logo-company">
          <img v-if="companyLogoUrl" :src="companyLogoUrl" alt="Client logo" class="logoHome" />
        </a>
        <span v-if="hasCliente && !companyLogoUrl" class="company-missing">Empresa não cadastrada</span>
        <div class="menu-navbar" v-if="!hasCliente">
          <a class="menuS" href="/" data-tour="nav-menu-empresa">Nossa empresa</a>
          <a class="menuS" href="/dna" data-tour="nav-menu-dna">Nosso DNA</a>
          <a class="menuS" href="/servicos" data-tour="nav-menu-servicos">Nossos servicos</a>
          <a class="menuS" href="/contato" data-tour="nav-menu-contato">Contato</a>
        </div>
        <div class="menu-navbar" v-else>
          <a
            v-if="menuVisibility.dataProtection"
            class="menuS"
            :class="{ active: isActive('/' + clienteParam + '/protecao-de-dados') }"
            :href="'/' + clienteParam + '/protecao-de-dados'"
            data-tour="nav-menu-dados"
          >{{ menuLabels.dataProtection }}</a>
          <a
            v-if="menuVisibility.complianceProgram"
            class="menuS"
            :class="{ active: isActive('/' + clienteParam + '/programa-de-compliance') }"
            :href="'/' + clienteParam + '/programa-de-compliance'"
            data-tour="nav-menu-compliance"
          >{{ menuLabels.complianceProgram }}</a>
          <a
            v-if="menuVisibility.complianceManual"
            class="menuS"
            :class="{ active: isActive('/' + clienteParam + '/manual-de-compliance') }"
            :href="'/' + clienteParam + '/manual-de-compliance'"
            data-tour="nav-menu-manual"
          >{{ menuLabels.complianceManual }}</a>
        </div>
      </div>

      <!-- Right: Settings + Profile menu -->
      <div class="nav-right" v-if="hasCliente">
        <button
          v-if="helpIconsEnabled && isLoggedIn && isCompanyAdmin"
          class="help-circle help-icon"
          @click.prevent="startNavTourRight"
          title="Tour de configurações e perfil"
          aria-label="Tour de configurações e perfil"
          type="button"
        >
          <i class="fas fa-question-circle" aria-hidden="true"></i>
        </button>
        <div v-if="isLoggedIn && isCompanyAdmin" class="settings-wrapper" ref="settingsMenu">
          <button
            class="settings-trigger"
            data-tour="settings-trigger"
            @click.prevent="toggleSettingsMenu"
            aria-label="Abrir menu de configurações"
            :aria-expanded="isSettingsMenuOpen ? 'true' : 'false'"
          >
            <i class="fas fa-gear" aria-hidden="true"></i>
          </button>
          <div v-if="isSettingsMenuOpen" class="settings-menu">
            <div v-if="isCompanyAdmin" class="settings-menu-section">Configurações</div>
            <button
              v-if="isCompanyAdmin"
              class="settings-menu-item"
              data-tour="settings-admin"
              @click="goToAdminSection('usuarios')"
            >Administração</button>
            <button
              v-if="isCompanyAdmin"
              class="settings-menu-item"
              data-tour="settings-cadastros"
              @click="goToCadastrosSection('cadastros')"
            >Cadastros</button>
            <button
              v-if="isCompanyAdmin"
              class="settings-menu-item"
              data-tour="settings-general"
              @click="goToGeneralSection('geral')"
            >Geral</button>
            <div v-if="hasInterviewModule || hasTerceirosModule || isCompanyAdmin" class="settings-menu-section">Módulos</div>
            <button
              v-if="hasInterviewModule"
              class="settings-menu-item"
              data-tour="settings-interview"
              @click="goToModuleSection('report', 'entrevista_com_ia')"
            >Entrevista com IA</button>
            <button
              v-if="hasTerceirosModule"
              class="settings-menu-item"
              data-tour="settings-terceiros"
              @click="goToModuleSection('risk', 'due_diligence_terceiros')"
            >Due Diligence de Terceiros</button>
            <button
              v-if="isCompanyAdmin"
              class="settings-menu-item"
              data-tour="settings-denuncias"
              @click="goToModuleSection('denuncias', 'canal_de_denuncias')"
            >Canal de Denúncias</button>
          </div>
        </div>
        <div v-if="isLoggedIn" class="profile-wrapper" ref="profileMenu">
          <button class="profile-trigger" data-tour="profile-trigger"
            @click.prevent="toggleProfileMenu"
            aria-label="Abrir menu do perfil"
            :aria-expanded="isProfileMenuOpen ? 'true' : 'false'"
          >
            <img v-if="resolvedAvatar" :src="resolvedAvatar" alt="Avatar do usuário" class="avatar-img" />
            <span v-else class="avatar-fallback">{{ profileInitials }}</span>
          </button>
          <div v-if="isProfileMenuOpen" class="profile-menu">
            <button class="profile-menu-item" data-tour="menu-perfil" @click="goToSettings">Perfil</button>
            <button v-if="supportActive" class="profile-menu-item" @click="endSupportSession">Encerrar suporte</button>
            <button
              class="profile-menu-item"
              :class="{ 'is-off': !helpIconsEnabled }"
              data-tour="menu-tutorial"
              @click="toggleHelpIcons"
              :title="helpIconsEnabled ? 'Ajuda ativada' : 'Ajuda desativada'"
            >
              <span>Tutorial</span>
            </button>
            <button v-if="hasSupportModule" class="profile-menu-item" data-tour="menu-suporte" @click="goToSupport">Suporte</button>
            <button v-if="showLogout" class="profile-menu-item" data-tour="menu-logout" @click="signOut">Sair</button>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { driver } from 'driver.js';
import { doc, getDoc, getDocFromServer, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { db } from '@/config/firebase';
import { getDueLogoUrl, getLogoUrl } from '@/utils/storageRefs.js';

export default {
  name: 'NavBarUnified',
  data(){
    return {
      dueLogoUrl: '',
      companyLogoUrl: '',
      isLoggedIn: false,
      isProfileMenuOpen: false,
      isSettingsMenuOpen: false,
      profileMenuLocked: false,
      settingsMenuLocked: false,
      avatarUrl: '',
      displayName: '',
      isCompanyAdmin: false,
      showDueLogo: true,
      ssoEnabled: false,
      ssoLogoutEnabled: false,
      ssoProvider: '',
      ssoLogoutUrl: '',
      ssoClientId: '',
      ssoLogoutRedirectUrl: '',
      ssoLogoutRedirectParam: '',
      ssoLogoutClientIdParam: '',
      avatarFromStorage: '',
      contractedModules: [],
      contractedSubmodules: {},
      menuLabels: {
        dataProtection: 'Proteção de Dados',
        complianceProgram: 'Programa de Compliance',
        complianceManual: 'Manual de Compliance',
      },
      menuVisibility: {
        dataProtection: true,
        complianceProgram: true,
        complianceManual: true,
      },
      helpIconsEnabled: true,
      helpIconsLoading: false,
    };
  },
  computed: {
    ...mapGetters(['user','company','supportSession','supportActive']),
    clienteParam(){
      // aceita ambos enquanto migra: :cliente preferencial, :company legado
      return (this.$route.params.cliente || this.$route.params.company || '').toString();
    },
    hasCliente(){ return !!this.clienteParam; },
    isMaterdei(){ return (this.clienteParam || '').toLowerCase() === 'materdei'; },
    isDue(){ return (this.clienteParam || '').toLowerCase() === 'due'; },
    canGoBack(){
      // habilita botão voltar quando há histórico ou quando não está na home pública
      return (window.history && window.history.length > 1) || this.hasCliente;
    },
    resolvedAvatar(){
      if (this.avatarUrl) return this.avatarUrl;
      if (this.avatarFromStorage) return this.avatarFromStorage;
      const effectiveUser = this.user || this.supportSession && this.supportSession.impersonatedEmail && { photoURL: '' };
      return effectiveUser?.photoURL || '';
    },
    resolvedDisplayName(){
      if (this.displayName) return this.displayName;
      if (this.supportSession?.impersonatedEmail) return this.supportSession.impersonatedEmail;
      const email = (this.user && (this.user.email || this.user.uid)) || '';
      return email;
    },
    showLogout(){
      return !this.ssoEnabled || this.ssoLogoutEnabled;
    },
    hasInterviewModule(){
      if (!this.hasCliente) return false;
      const normalized = Array.isArray(this.contractedModules)
        ? this.contractedModules.map((m) => m.toString().toLowerCase())
        : [];
      const submods = this.contractedSubmodules || {};
      const interviewSubs = Array.isArray(submods.entrevista_com_ia) ? submods.entrevista_com_ia : [];
      return normalized.includes('entrevista_com_ia') || interviewSubs.length > 0;
    },
    hasTerceirosModule(){
      if (!this.hasCliente) return false;
      const normalized = Array.isArray(this.contractedModules)
        ? this.contractedModules.map((m) => m.toString().toLowerCase())
        : [];
      return normalized.includes('due_diligence_terceiros') || normalized.includes('terceiros');
    },
    hasDenunciasModule(){
      if (!this.hasCliente) return false;
      const normalized = Array.isArray(this.contractedModules)
        ? this.contractedModules.map((m) => m.toString().toLowerCase())
        : [];
      return normalized.includes('canal_de_denuncias') || normalized.includes('denuncias');
    },
    hasSupportModule(){
      if (!this.hasCliente) return false;
      const normalized = Array.isArray(this.contractedModules)
        ? this.contractedModules.map((m) => m.toString().toLowerCase())
        : [];
      return normalized.includes('suporte');
    },
    profileInitials(){
      const base = (this.resolvedDisplayName || '').toString().trim();
      if (!base) return '?';
      const parts = base.split(/\s+/).filter(Boolean);
      const initials = parts.length >= 2
        ? `${parts[0][0]}${parts[1][0]}`
        : `${parts[0][0]}`;
      return initials.toUpperCase();
    }
  },
  mounted(){
    this.loadDueLogo();
    if (this.hasCliente) this.loadCompanyLogo();
    if (this.hasCliente) this.loadCompanySettings();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    this.isLoggedIn = !!currentUser || this.supportActive;
    if (currentUser) this.loadUserProfile(currentUser);
    onAuthStateChanged(auth, async (u) => {
      this.isLoggedIn = !!u || this.supportActive;
      await this.loadUserProfile(u);
    });
    this.syncHelpIconsAttribute();
    document.addEventListener('click', this.handleClickOutside);
    window.__dueOpenProfileMenu = () => { this.isProfileMenuOpen = true; this.profileMenuLocked = true; };
    window.__dueCloseProfileMenu = () => { this.isProfileMenuOpen = false; this.profileMenuLocked = false; };
    window.__dueOpenSettingsMenu = () => { this.isSettingsMenuOpen = true; this.settingsMenuLocked = true; };
    window.__dueCloseSettingsMenu = () => { this.isSettingsMenuOpen = false; this.settingsMenuLocked = false; };
  },
  beforeUnmount(){
    document.removeEventListener('click', this.handleClickOutside);
    if (window.__dueOpenProfileMenu) delete window.__dueOpenProfileMenu;
    if (window.__dueCloseProfileMenu) delete window.__dueCloseProfileMenu;
    if (window.__dueOpenSettingsMenu) delete window.__dueOpenSettingsMenu;
    if (window.__dueCloseSettingsMenu) delete window.__dueCloseSettingsMenu;
  },
  watch: {
    '$route.params.company'(){
      if (!this.hasCliente) return;
      this.loadCompanyLogo();
      this.loadUserProfile(this.user);
    },
    '$route.params.cliente'(){
      if (!this.hasCliente) return;
      this.loadCompanyLogo();
      this.loadUserProfile(this.user);
    },
    company(newVal){
      if (!newVal) return;
      this.loadCompanyLogo();
      this.loadUserProfile(this.user);
    },
    user(newVal){
      this.isLoggedIn = !!newVal || this.supportActive;
      this.loadUserProfile(newVal);
    }
  },
  methods: {
    ...mapActions(['logOut','clearSupportSession']),
    resolveAccountId(slug){
      return `acc_${(slug || '').toString().trim().toLowerCase()}`;
    },
    async resolvePublicLogoCandidate(rawUrl){
      const candidate = (rawUrl || '').toString().trim();
      if (!candidate) return '';
      const normalizedCandidate = candidate.toLowerCase();

      // Ignora caminhos legados removidos da plataforma.
      if (
        normalizedCandidate.startsWith('cliente/')
        || normalizedCandidate.startsWith('/cliente/')
        || normalizedCandidate.startsWith('publico/identidade-visual/')
      ) {
        return '';
      }

      // Caminho de objeto do Storage sem URL absoluta.
      if (!candidate.startsWith('http://') && !candidate.startsWith('https://') && !candidate.startsWith('gs://')) {
        try {
          const storage = getStorage();
          return await getDownloadURL(storageRef(storage, candidate.replace(/^\/+/, '')));
        } catch (_) {
          return '';
        }
      }

      // Se vier em gs://, converte para URL assinada via SDK.
      if (candidate.startsWith('gs://')) {
        try {
          const storage = getStorage();
          const gsPath = candidate.replace(/^gs:\/\/[^/]+\//, '');
          const normalizedGsPath = gsPath.toLowerCase();
          if (!gsPath) return candidate;
          if (normalizedGsPath.startsWith('cliente/') || normalizedGsPath.startsWith('publico/identidade-visual/')) return '';
          return await getDownloadURL(storageRef(storage, gsPath));
        } catch (_) {
          return '';
        }
      }

      // Corrige URL legada sem token no formato /v0/b/<bucket>/o/<path>.
      try {
        const parsed = new URL(candidate);
        const isFirebaseStorageHost = parsed.hostname === 'firebasestorage.googleapis.com';
        const objectMatch = parsed.pathname.match(/\/v0\/b\/[^/]+\/o\/(.+)$/);
        const hasDownloadToken = parsed.searchParams.has('token');
        if (isFirebaseStorageHost && objectMatch) {
          const objectPath = decodeURIComponent(objectMatch[1] || '');
          const normalizedObjectPath = objectPath.toLowerCase();
          if (!objectPath) return candidate;
          if (normalizedObjectPath.startsWith('publico/identidade-visual/')) return '';
          if (normalizedObjectPath.startsWith('cliente/')) return '';
          if (hasDownloadToken) return candidate;
          try {
            const storage = getStorage();
            return await getDownloadURL(storageRef(storage, objectPath));
          } catch (_) {
            // URL sem token para Storage não deve ser reaproveitada.
            return '';
          }
        }
      } catch (_) {
        // Ignora erro de parse e retorna como veio.
      }

      return candidate;
    },
    toggleSettingsMenu(){
      this.isSettingsMenuOpen = !this.isSettingsMenuOpen;
      if (this.isSettingsMenuOpen) this.isProfileMenuOpen = false;
    },
    toggleProfileMenu(){
      this.isProfileMenuOpen = !this.isProfileMenuOpen;
      if (this.isProfileMenuOpen) this.isSettingsMenuOpen = false;
    },
    handleClickOutside(event){
      if (this.isProfileMenuOpen && !this.profileMenuLocked) {
        const profileRoot = this.$refs.profileMenu;
        if (!profileRoot || !profileRoot.contains(event.target)) this.isProfileMenuOpen = false;
      }
      if (this.isSettingsMenuOpen && !this.settingsMenuLocked) {
        const settingsRoot = this.$refs.settingsMenu;
        if (!settingsRoot || !settingsRoot.contains(event.target)) this.isSettingsMenuOpen = false;
      }
    },
    goToSettings(){
      this.isProfileMenuOpen = false;
      if (this.hasCliente) this.$router.push('/' + this.clienteParam + '/perfil');
    },
    goToGeneralSection(section){
      this.isSettingsMenuOpen = false;
      const targetSection = section ? `&section=${section}` : '';
      if (this.hasCliente) this.$router.push('/' + this.clienteParam + `/sistema?scope=general${targetSection}`);
    },
    goToAdminSection(section){
      this.isSettingsMenuOpen = false;
      const targetSection = section ? `&section=${section}` : '';
      if (this.hasCliente) this.$router.push('/' + this.clienteParam + `/sistema?scope=admin${targetSection}`);
    },
    goToCadastrosSection(section){
      this.isSettingsMenuOpen = false;
      if (this.hasCliente) this.$router.push('/' + this.clienteParam + '/sistema?scope=cadastros&section=' + section);
    },
    goToModuleSection(section, moduleId = 'entrevista_com_ia'){
      this.isSettingsMenuOpen = false;
      if (this.hasCliente) this.$router.push('/' + this.clienteParam + `/sistema?scope=module&module=${moduleId}&section=` + section);
    },
    async endSupportSession(){
      this.isProfileMenuOpen = false;
      try {
        const session = this.supportSession || null;
        const company = (session && session.company) || this.clienteParam;
        const sessionId = session && session.sessionId;
        if (company && sessionId) {
          await updateDoc(doc(db, `accounts/${this.resolveAccountId(company)}/sessions/${sessionId}`), {
            endedAt: serverTimestamp(),
            endedReason: 'menu'
          });
        }
      } catch (_) {
        // ignore
      } finally {
        localStorage.removeItem('supportLoginExpiresAtMs');
        await this.logOut();
        this.isLoggedIn = false;
        this.isProfileMenuOpen = false;
        this.$router.push('/admin/suporte/login');
      }
    },

    async signOut(){
      await this.logOut();
      this.isLoggedIn = false;
      let allowCachedLogout = true;
      if (this.hasCliente) {
        try {
          const slug = (this.clienteParam || '').toString().trim().toLowerCase();
          if (slug) {
            const ssoSnap = await getDocFromServer(doc(db, `accounts/${this.resolveAccountId(slug)}/config/sso`));
            if (ssoSnap.exists()) {
              const ssoData = ssoSnap.data() || {};
              if (ssoData.enabled !== true) {
                allowCachedLogout = false;
              }
              const provider = (ssoData.provider || '').toString().trim().toLowerCase();
              const singleLogoutUrl = (ssoData.singleLogoutServiceUrl || '').toString().trim();
              const endSessionUrl = (ssoData.endSessionEndpoint || ssoData.endSessionUrl || '').toString().trim();
              const logoutUrl = singleLogoutUrl || (provider === 'oidc' ? endSessionUrl : '');
              if (logoutUrl) {
                const origin = window.location.origin || '';
                const fallbackRedirect = (ssoData.postLogoutRedirectUrl || ssoData.logoutRedirectUrl || '').toString().trim()
                  || (origin ? `${origin}/${this.clienteParam}` : '');
                const redirectParam = (ssoData.logoutRedirectParam || '').toString().trim()
                  || (provider === 'oidc' ? 'post_logout_redirect_uri' : '');
                const clientParam = (ssoData.logoutClientIdParam || '').toString().trim() || 'client_id';
                const clientId = (ssoData.clientId || '').toString().trim();
                try {
                  const nextUrl = new URL(logoutUrl);
                  if (redirectParam && fallbackRedirect) nextUrl.searchParams.set(redirectParam, fallbackRedirect);
                  if (clientId) nextUrl.searchParams.set(clientParam, clientId);
                  window.location.href = nextUrl.toString();
                  return;
                } catch (_) {
                  // ignore and fallback
                }
              } else {
                allowCachedLogout = false;
              }
            } else {
              allowCachedLogout = false;
            }
          }
        } catch (_) {
          // ignore and fallback
          allowCachedLogout = false;
        }
      }
      if (this.hasCliente && allowCachedLogout && this.ssoLogoutEnabled && this.ssoLogoutUrl) {
        const origin = window.location.origin || '';
        const fallbackRedirect = this.ssoLogoutRedirectUrl || (origin ? `${origin}/${this.clienteParam}` : '');
        try {
          const logoutUrl = new URL(this.ssoLogoutUrl);
          const redirectParam = this.ssoLogoutRedirectParam
            || (this.ssoProvider === 'oidc' ? 'post_logout_redirect_uri' : '');
          if (redirectParam && fallbackRedirect) {
            logoutUrl.searchParams.set(redirectParam, fallbackRedirect);
          }
          const clientParam = this.ssoLogoutClientIdParam || 'client_id';
          if (this.ssoClientId) logoutUrl.searchParams.set(clientParam, this.ssoClientId);
          window.location.href = logoutUrl.toString();
          return;
        } catch (_) {
          // ignore and fallback
        }
      }
      if (this.hasCliente) this.$router.push('/' + this.clienteParam + '/entrar?sso=0');
      else this.$router.push('/');
    },
    async loadDueLogo(){
      try{ this.dueLogoUrl = await getDueLogoUrl(); }catch(_){ this.dueLogoUrl = ''; }
    },
    async loadCompanyLogo(){
      try{
        const c = (this.clienteParam || '').toString().trim();
        if (!c || c.toLowerCase() === 'company') { this.companyLogoUrl = ''; return; }
        this.companyLogoUrl = '';
        try {
          const snap = await getDoc(doc(db, `accounts/${this.resolveAccountId(c)}`));
          if (snap.exists()) {
            const data = snap.data() || {};
            const fallback = await this.resolvePublicLogoCandidate(data.image || data.logoUrl || data.logo);
            if (fallback) this.companyLogoUrl = fallback;
          }
        } catch (_) {
          // ignore fallback errors
        }
        if (!this.companyLogoUrl) {
          this.companyLogoUrl = await getLogoUrl(c);
        }
      }catch(_){ this.companyLogoUrl = ''; }
      await this.loadCompanySettings();
    },
    async loadCompanySettings(){
      try{
        const slug = (this.clienteParam || '').toString().trim().toLowerCase();
        if (!slug) { this.showDueLogo = true; this.ssoEnabled = false; this.ssoLogoutEnabled = false; return; }
        const accountId = this.resolveAccountId(slug);
        const snap = await getDoc(doc(db, `accounts/${accountId}`));
        const data = snap.exists() ? (snap.data() || {}) : {};
        if (!this.companyLogoUrl) {
          const publicLogo = await this.resolvePublicLogoCandidate(data.image || data.logoUrl || data.logo);
          if (publicLogo) this.companyLogoUrl = publicLogo;
        }
        this.showDueLogo = data.showDueLogo !== false;
        this.contractedModules = Array.isArray(data.contractedModules)
          ? data.contractedModules.map((m) => m.toString().toLowerCase())
          : [];
        this.contractedSubmodules = data.contractedSubmodules || {};
        try {
          const uiSnap = await getDoc(doc(db, `accounts/${accountId}/config/ui`));
          const uiData = uiSnap.exists() ? (uiSnap.data() || {}) : {};
          const labels = uiData.labels || {};
          const visibility = uiData.menuVisibility || {};
          this.menuLabels = {
            dataProtection: (labels.dataProtection || this.menuLabels.dataProtection).toString(),
            complianceProgram: (labels.complianceProgram || this.menuLabels.complianceProgram).toString(),
            complianceManual: (labels.complianceManual || this.menuLabels.complianceManual).toString(),
          };
          this.menuVisibility = {
            dataProtection: visibility.dataProtection !== false,
            complianceProgram: visibility.complianceProgram !== false,
            complianceManual: visibility.complianceManual !== false,
          };
        } catch (_) {
          this.menuLabels = {
            dataProtection: 'Proteção de Dados',
            complianceProgram: 'Programa de Compliance',
            complianceManual: 'Manual de Compliance',
          };
          this.menuVisibility = {
            dataProtection: true,
            complianceProgram: true,
            complianceManual: true,
          };
        }
        try {
          const ssoSnap = await getDoc(doc(db, `accounts/${accountId}/config/sso`));
          const ssoData = ssoSnap.exists() ? (ssoSnap.data() || {}) : {};
          this.ssoEnabled = ssoData.enabled === true;
          this.ssoProvider = (ssoData.provider || '').toString().trim().toLowerCase();
          const singleLogoutUrl = (ssoData.singleLogoutServiceUrl || '').toString().trim();
          const endSessionUrl = (ssoData.endSessionEndpoint || ssoData.endSessionUrl || '').toString().trim();
          this.ssoLogoutUrl = singleLogoutUrl || (this.ssoProvider === 'oidc' ? endSessionUrl : '');
          this.ssoClientId = (ssoData.clientId || '').toString().trim();
          this.ssoLogoutRedirectUrl = (ssoData.postLogoutRedirectUrl || ssoData.logoutRedirectUrl || '').toString().trim();
          this.ssoLogoutRedirectParam = (ssoData.logoutRedirectParam || '').toString().trim();
          this.ssoLogoutClientIdParam = (ssoData.logoutClientIdParam || '').toString().trim();
          this.ssoLogoutEnabled = !!this.ssoLogoutUrl;
        } catch (_) {
          this.ssoEnabled = false;
          this.ssoLogoutEnabled = false;
          this.ssoProvider = '';
          this.ssoLogoutUrl = '';
          this.ssoClientId = '';
          this.ssoLogoutRedirectUrl = '';
          this.ssoLogoutRedirectParam = '';
          this.ssoLogoutClientIdParam = '';
        }
      } catch (_) {
        this.showDueLogo = true;
        this.ssoEnabled = false;
        this.ssoLogoutEnabled = false;
        this.ssoProvider = '';
        this.ssoLogoutUrl = '';
        this.ssoClientId = '';
        this.ssoLogoutRedirectUrl = '';
        this.ssoLogoutRedirectParam = '';
        this.ssoLogoutClientIdParam = '';
        this.contractedModules = [];
        this.contractedSubmodules = {};
        this.menuLabels = {
          dataProtection: 'Proteção de Dados',
          complianceProgram: 'Programa de Compliance',
          complianceManual: 'Manual de Compliance',
        };
        this.menuVisibility = {
          dataProtection: true,
          complianceProgram: true,
          complianceManual: true,
        };
      }
    },
    async loadUserProfile(user){
      this.avatarUrl = '';
      this.displayName = '';
      this.isCompanyAdmin = false;
      this.helpIconsEnabled = true;
      if (!this.hasCliente) return;

      if (this.supportActive && this.supportSession) {
        const slug = (this.clienteParam || '').toString().toLowerCase();
        const supportEmail = (this.supportSession.supportEmail || '').toLowerCase();
        const impersonated = (this.supportSession.impersonatedEmail || '').toLowerCase();
        const targetEmail = impersonated || supportEmail;
        this.displayName = targetEmail || 'Suporte';
        this.isCompanyAdmin = false;

        if (impersonated && slug) {
          try {
            const accountId = this.resolveAccountId(slug);
            const snap = await getDoc(doc(db, `accounts/${accountId}/users/${impersonated}`));
            if (snap.exists()) {
              const data = snap.data() || {};
              this.displayName = data.name || this.displayName;
              this.avatarUrl = data.avatarUrl || '';
              this.helpIconsEnabled = data.helpIconsEnabled !== false;
            }
            const cfgSnap = await getDoc(doc(db, `accounts/${accountId}/users/${impersonated}/modules/config`));
            if (cfgSnap.exists()) {
              const cfg = cfgSnap.data() || {};
              this.isCompanyAdmin = !!(cfg.admin && cfg.admin.enabled === true);
            }
          } catch (_) {
            this.isCompanyAdmin = false;
          }
        }
        this.syncHelpIconsAttribute();
        return;
      }

      const effectiveUser = user || this.user;
      if (!effectiveUser) return;
      const storeEmail = (this.$store?.getters?.userEmail || this.$store?.getters?.email || '').toString().toLowerCase();
      const uidEmail = (effectiveUser.uid && effectiveUser.uid.includes('@')) ? effectiveUser.uid : '';
      const email = (effectiveUser.email || uidEmail || storeEmail || '').toString().toLowerCase();
      if (!email) return;
      this.displayName = effectiveUser.displayName || this.displayName || email;
      try {
        const slug = (this.clienteParam || '').toString().toLowerCase();
        const accountId = this.resolveAccountId(slug);
        const snap = await getDoc(doc(db, `accounts/${accountId}/users/${email}`));
        if (snap.exists()) {
          const data = snap.data() || {};
          this.displayName = data.name || this.displayName;
          this.avatarUrl = data.avatarUrl || effectiveUser.photoURL || '';
          const roles = Array.isArray(data.roles) ? data.roles : [];
          this.isCompanyAdmin = data.role === 'admin' || roles.includes('admin');
          this.helpIconsEnabled = data.helpIconsEnabled !== false;
        } else {
          this.avatarUrl = effectiveUser.photoURL || '';
        }

        try {
          const cfgSnap = await getDoc(doc(db, `accounts/${accountId}/users/${email}/modules/config`));
          if (cfgSnap.exists()) {
            const cfg = cfgSnap.data() || {};
            this.isCompanyAdmin = this.isCompanyAdmin || !!(cfg.admin && cfg.admin.enabled === true);
          }
        } catch (_) {
          this.isCompanyAdmin = this.isCompanyAdmin || false;
        }
        this.syncHelpIconsAttribute();
      } catch (error) {
        console.warn('[NAVBAR] erro ao carregar avatar', { email, error });
        this.avatarUrl = effectiveUser.photoURL || '';
      }
    },
    goToSupport(){
      if (!this.hasCliente) return;
      this.$router.push(`/${this.clienteParam}/suporte`);
      this.isProfileMenuOpen = false;
    },
    syncHelpIconsAttribute(){
      const root = document.documentElement;
      if (!root) return;
      root.setAttribute('data-help-icons', this.helpIconsEnabled ? 'on' : 'off');
    },
    async toggleHelpIcons(){
      if (this.helpIconsLoading) return;
      const slug = (this.clienteParam || '').toString().trim().toLowerCase();
      if (!slug) return;
      this.isProfileMenuOpen = false;
      const effectiveUser = this.user || (this.supportSession && this.supportSession.impersonatedEmail ? { email: '' } : null);
      const storeEmail = (this.$store?.getters?.userEmail || this.$store?.getters?.email || '').toString().toLowerCase();
      const uidEmail = (effectiveUser && effectiveUser.uid && effectiveUser.uid.includes('@')) ? effectiveUser.uid : '';
      const email = (effectiveUser && (effectiveUser.email || uidEmail) || storeEmail || '').toString().toLowerCase();
      if (!email) return;
      this.helpIconsLoading = true;
      const nextValue = !this.helpIconsEnabled;
      try {
        const accountId = this.resolveAccountId(slug);
        await updateDoc(doc(db, `accounts/${accountId}/users/${email}`), {
          helpIconsEnabled: nextValue,
          helpIconsUpdatedAt: serverTimestamp(),
        });
        this.helpIconsEnabled = nextValue;
      } catch (error) {
        try {
          const accountId = this.resolveAccountId(slug);
          await setDoc(doc(db, `accounts/${accountId}/users/${email}`), {
            helpIconsEnabled: nextValue,
            helpIconsUpdatedAt: serverTimestamp(),
          }, { merge: true });
          this.helpIconsEnabled = nextValue;
        } catch (_) {
          // ignore update failure
        }
      } finally {
        this.helpIconsLoading = false;
        this.syncHelpIconsAttribute();
      }
    },
    openProfileMenu(){
      if (window.__dueOpenProfileMenu) {
        window.__dueOpenProfileMenu();
      }
    },
    closeProfileMenu(){
      if (window.__dueCloseProfileMenu) {
        window.__dueCloseProfileMenu();
      }
    },
    openSettingsMenu(){
      if (window.__dueOpenSettingsMenu) {
        window.__dueOpenSettingsMenu();
      }
    },
    closeSettingsMenu(){
      if (window.__dueCloseSettingsMenu) {
        window.__dueCloseSettingsMenu();
      }
    },
    async startNavTourLeft(){
      const steps = [
        {
          element: '[data-tour="nav-back"]',
          popover: {
            title: 'Voltar',
            description: 'Retorne para a tela anterior.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-tour="nav-logo-company"]',
          popover: {
            title: 'Logo do cliente',
            description: 'Acesso rápido à home do cliente.',
            side: 'bottom',
            align: 'center',
          },
        },
        {
          element: '[data-tour="nav-menu-dados"]',
          popover: {
            title: 'Proteção de Dados',
            description: 'Acesso aos termos de Proteção de Dados.',
            side: 'bottom',
            align: 'center',
          },
        },
        {
          element: '[data-tour="nav-menu-compliance"]',
          popover: {
            title: 'Programa de Compliance',
            description: 'Acesso ao programa de Compliance.',
            side: 'bottom',
            align: 'center',
          },
        },
        {
          element: '[data-tour="nav-menu-manual"]',
          popover: {
            title: 'Manual de Compliance',
            description: 'Consulte o manual e documentos relacionados.',
            side: 'bottom',
            align: 'center',
          },
        },
      ];

      if (this.showDueLogo) {
        steps.splice(1, 0, {
          element: '[data-tour="nav-logo-due"]',
          popover: {
            title: 'Logo DUE',
            description: 'Volta para a página inicial.',
            side: 'bottom',
            align: 'center',
          },
        });
      }

      const filtered = steps.filter(step => document.querySelector(step.element));

      if (!filtered.length) {
        return;
      }

      const tour = driver({
        showProgress: true,
        steps: filtered,
      });
      tour.drive();
    },
    async startNavTourRight(){
      this.openSettingsMenu();
      await new Promise(resolve => setTimeout(resolve, 200));
      const settingsSteps = [
        {
          element: '[data-tour="settings-trigger"]',
          popover: {
            title: 'Configurações',
            description: 'Abra o menu de configurações para ajustes gerais e por módulo.',
            side: 'bottom',
            align: 'end',
          },
        },
        {
          element: '[data-tour="settings-admin"]',
          popover: {
            title: 'Administração',
            description: 'Área administrativa do cliente (visível apenas para admins).',
            side: 'left',
            align: 'start',
          },
        },
        {
          element: '[data-tour="settings-cadastros"]',
          popover: {
            title: 'Cadastros',
            description: 'Gerencie empresas do grupo e setores.',
            side: 'left',
            align: 'start',
          },
        },
        {
          element: '[data-tour="settings-general"]',
          popover: {
            title: 'Configurações gerais',
            description: 'Identidade visual, autenticação e auditoria.',
            side: 'left',
            align: 'start',
          },
        },
        {
          element: '[data-tour="settings-interview"]',
          popover: {
            title: 'Entrevista com IA',
            description: 'Configurações do módulo de entrevistas.',
            side: 'left',
            align: 'start',
          },
        },
        {
          element: '[data-tour="settings-terceiros"]',
          popover: {
            title: 'Due Diligence de Terceiros',
            description: 'Configure pesos de risco, SLAs e checagem periódica.',
            side: 'left',
            align: 'start',
          },
        },
        {
          element: '[data-tour="settings-denuncias"]',
          popover: {
            title: 'Canal de Denúncias',
            description: 'Configure os destinatários do canal de denúncias.',
            side: 'left',
            align: 'start',
          },
        },
      ].filter(step => document.querySelector(step.element));

      if (!settingsSteps.length) {
        this.closeSettingsMenu();
      }
      if (!settingsSteps.length) {
        this.closeProfileMenu();
      }

      const runProfileTour = async () => {
        this.openProfileMenu();
        await new Promise(resolve => setTimeout(resolve, 150));
        const profileSteps = [
          {
            element: '[data-tour="profile-trigger"]',
            popover: {
              title: 'Menu do perfil',
              description: 'Abra o menu do usuário para acessar atalhos e configurações.',
              side: 'bottom',
              align: 'end',
            },
          },
          {
            element: '[data-tour="menu-perfil"]',
            popover: {
              title: 'Perfil',
              description: 'Altere seus dados pessoais e avatar.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="menu-tutorial"]',
            popover: {
              title: 'Ajuda',
              description: 'Ative ou desative os ícones de tour.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="menu-suporte"]',
            popover: {
              title: 'Suporte',
              description: 'Abra ou acompanhe tickets de suporte.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="menu-logout"]',
            popover: {
              title: 'Sair',
              description: 'Encerramento da sessão do usuário.',
              side: 'left',
              align: 'start',
            },
          },
        ].filter(step => document.querySelector(step.element));

        if (!profileSteps.length) return;
        const profileTour = driver({
          showProgress: true,
          steps: profileSteps,
          onDestroyStarted: () => {
            this.closeProfileMenu();
            profileTour.destroy();
          },
        });
        profileTour.drive();
      };

      if (!settingsSteps.length) {
        await runProfileTour();
        return;
      }

      const tour = driver({
        showProgress: true,
        steps: settingsSteps,
        onDestroyStarted: async () => {
          this.closeSettingsMenu();
          tour.destroy();
          await runProfileTour();
        },
      });
      tour.drive();
    },
    async fetchAvatarFromStorage(slug, email) {
      if (!slug || !email) return;
      const safeEmail = encodeURIComponent(email);
      const storage = getStorage();
      const extensions = ['png', 'jpg', 'jpeg'];
      for (const ext of extensions) {
        try {
          const accountId = this.resolveAccountId(slug);
          const url = await getDownloadURL(storageRef(storage, `accounts/${accountId}/users/${safeEmail}/avatar.${ext}`));
          this.avatarFromStorage = url;
          return;
        } catch (_) {
          // tenta próxima extensão
        }
      }
    },
    isActive(prefix){
      try { return (this.$route && this.$route.path || '').startsWith(prefix); } catch(_) { return false; }
    },
    goBack(){
      if (window.history && window.history.length > 1) this.$router.back();
      else if (this.hasCliente) this.$router.push('/' + this.clienteParam);
      else this.$router.push('/');
    }
  }
}
</script>

<style scoped>
.menuS{ 
  margin: 0 8px; 
  text-decoration: none; 
  font-size: 16px; 
  color:var(--color-title); 
  font-weight:600; 
  padding:10px 14px; 
  border-radius:10px; 
  position: relative; 
  transition: color .2s ease, background .2s ease, box-shadow .2s ease;
}
.menuS::after{
  content:''; position:absolute; left:12px; right:12px; bottom:6px; height:2px;
  background:var(--color-primary); transform: scaleX(0); transform-origin: left; transition: transform .2s ease;
}
.menuS:hover{ background: rgba(8,131,149,.08); }
.menuS:hover::after{ transform: scaleX(1); }
.menuS:focus-visible{ outline:2px solid rgba(8,131,149,.45); outline-offset:2px; }
.menuS.active{ background:var(--color-primary); color:var(--color-primary-contrast); box-shadow:0 2px 10px rgba(8,131,149,.25); }
.menuS.active::after{ background: var(--color-primary-contrast); transform: scaleX(1); }
.logoHome{ max-height: 60px; margin: 0 16px; margin-top: 12px; }
.menu-navbar{ display: flex; align-items: center; gap: 4px; }
.navbar-main{ display:flex; justify-content:space-between; align-items:center; width: 100%; padding: 0 8px; }
.headerS{ display:flex; justify-content:center; background:var(--color-surface); position:fixed; top:0; left:0; right:0; width:100%; z-index:5; height:100px; }
.company-missing{ font-size: 14px; color: #b00020; margin-left: 8px; }
.back-link{ display:flex; align-items:center; margin-left:4px; text-decoration:none; background: transparent; border: none; color:var(--color-title); font-size: 20px; cursor: pointer; padding: 4px; }
.back-link:hover{ color:var(--color-primary); }
.back-link:focus-visible{ outline:2px solid rgba(8,131,149,.45); outline-offset:2px; border-radius:6px; }
.nav-left{ min-width: 60px; display:flex; align-items:center; justify-content:flex-start; }
.nav-center{ flex:1; display:flex; align-items:center; justify-content:center; }
.nav-right{ min-width: 160px; display:flex; align-items:center; justify-content:flex-end; padding-right:4px; gap: 8px; }
.settings-wrapper{ position: relative; }
.profile-menu-item.is-off{ color: var(--color-icon-inactive); }
.settings-trigger{
  display:flex;
  align-items:center;
  justify-content:center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  cursor: pointer;
  color: var(--color-title);
  font-size: 16px;
}
.settings-trigger:focus-visible{ outline:2px solid rgba(8,131,149,.45); outline-offset:2px; }
.settings-menu{
  position: absolute;
  right: 0;
  top: 50px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(10,34,43,.12);
  min-width: 220px;
  overflow: hidden;
  z-index: 20;
}
.settings-menu-header{
  padding: 10px 14px;
  font-weight: 700;
  color: var(--color-title);
  border-bottom: 1px solid var(--color-border);
}
.settings-menu-section{
  padding: 8px 14px 4px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: var(--color-muted);
}
.settings-menu-item{
  width: 100%;
  padding: 10px 14px;
  text-align: left;
  background: transparent;
  border: none;
  color: var(--color-text);
  font-weight: 600;
  cursor: pointer;
}
.settings-menu-item:hover{ background: rgba(8,131,149,.08); }
.settings-menu-item:disabled{
  opacity: .5;
  cursor: not-allowed;
}
.settings-menu-item:disabled:hover{ background: transparent; }
.profile-wrapper{ position: relative; display:flex; align-items:center; gap: 8px; }
.help-circle{
  display:flex;
  align-items:center;
  justify-content:center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: transparent;
  cursor: pointer;
  color: var(--color-title);
  font-size: 16px;
}
.help-circle:focus-visible{ outline:2px solid rgba(8,131,149,.45); outline-offset:2px; }
.profile-trigger{
  display:flex;
  align-items:center;
  justify-content:center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  cursor: pointer;
  padding: 0;
}
.profile-trigger:focus-visible{ outline:2px solid rgba(8,131,149,.45); outline-offset:2px; }
.avatar-img{
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.avatar-fallback{
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-primary);
  color: var(--color-primary-contrast);
  font-weight: 700;
  display:flex;
  align-items:center;
  justify-content:center;
  letter-spacing: .5px;
}
.profile-menu{
  position: absolute;
  right: 0;
  top: 50px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(10,34,43,.12);
  min-width: 180px;
  overflow: hidden;
  z-index: 20;
}
.profile-menu-item{
  width: 100%;
  padding: 10px 14px;
  text-align: left;
  background: transparent;
  border: none;
  color: var(--color-text);
  font-weight: 600;
  cursor: pointer;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 12px;
}
.profile-menu-item:hover{ background: rgba(8,131,149,.08); }
</style>
